/**
 * ActivityPage — player-facing summary of all payment activity.
 *
 * Consolidates three data sources into one surface so spares and
 * season-fee payers don't have to dig through Profile to find what
 * they've paid and what credit they have left:
 *
 *   1. Credit balance (per currency) — the amount of unspent credit
 *      sitting on their account, ready to auto-apply the next time
 *      they check into a paid spare game on the same team.
 *   2. Payment history — every spare_fee / season_fee row they've
 *      ever paid (or started paying), across all teams. Hydrated
 *      with team + game date on the server so each row renders as
 *      a clean "$X · Team · May 10" line.
 *   3. Credit history — audit trail of credits minted, used, and
 *      refunded. Supports a "Refund to card" action for available
 *      credits via POST /credits/:id/refund.
 *
 * The nav item that points here is conditional on
 * user.hasPaymentActivity (set by /auth/me). Users who've never
 * touched paid spares see no nav link and no page — the surface only
 * exists once it's actually relevant.
 *
 * This page intentionally lives OUTSIDE the team-scoped route tree
 * because credits and payments are user-scoped (a spare can play
 * across multiple teams and their credit carries between them).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Receipt, RefreshCw, ChevronRight } from 'lucide-react';
import api from '../services/api.js';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────
// Formatting helpers — all dollar-denominated display goes through
// these so any future currency (EUR, GBP) picks up the correct
// uppercase formatting without edits elsewhere.
// ─────────────────────────────────────────────────────────────────
const fmtAmount = (cents, currency) =>
  `$${((cents || 0) / 100).toFixed(2)} ${(currency || 'cad').toUpperCase()}`;

const fmtDate = (yyyymmdd) => {
  if (!yyyymmdd) return null;
  // Server returns DATE strings in ISO form; parse at noon UTC so
  // the toLocaleDateString doesn't slip into the previous day in
  // western hemisphere timezones.
  const iso = typeof yyyymmdd === 'string' ? yyyymmdd.slice(0, 10) : yyyymmdd;
  try {
    return new Date(iso + 'T12:00:00Z').toLocaleDateString('en-CA', {
      month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
  } catch {
    return String(iso);
  }
};

const fmtTimestamp = (isoStamp) => {
  if (!isoStamp) return null;
  try {
    return new Date(isoStamp).toLocaleDateString('en-CA', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return null;
  }
};

// Payment-type label shown above each payment row. payment_type is
// an internal identifier; surface user-friendly labels.
const PAYMENT_TYPE_LABELS = {
  spare_fee:   'Spare Fee',
  season_fee:  'Season Fee',
  game_fee:    'Game Fee',
  member_fee:  'Member Fee',
};
const paymentTypeLabel = (t) => PAYMENT_TYPE_LABELS[t] || 'Payment';

// Status badge palette. Only three states we display: paid,
// pending (Stripe session started but not completed), failed.
const PAYMENT_STATUS_BADGE = {
  paid:     { text: 'Paid',     bg: 'rgba(0,232,122,0.10)', fg: 'var(--goal)',   border: 'rgba(0,232,122,0.35)' },
  pending:  { text: 'Pending',  bg: 'rgba(255,149,0,0.10)', fg: 'var(--puck)',   border: 'rgba(255,149,0,0.35)' },
  failed:   { text: 'Failed',   bg: 'rgba(255,68,68,0.10)', fg: 'var(--penalty)', border: 'rgba(255,68,68,0.35)' },
};

const CREDIT_STATUS_BADGE = {
  available: { text: 'Available', bg: 'rgba(0,232,122,0.10)', fg: 'var(--goal)',  border: 'rgba(0,232,122,0.35)' },
  applied:   { text: 'Used',      bg: 'rgba(167,139,250,0.10)', fg: '#a78bfa',    border: 'rgba(167,139,250,0.35)' },
  refunded:  { text: 'Refunded',  bg: 'var(--surface-2)',       fg: 'var(--text-2)', border: 'var(--border)' },
};

export default function ActivityPage() {
  const navigate = useNavigate();
  const [loading,       setLoading]       = useState(true);
  const [payments,      setPayments]      = useState([]);
  const [creditBalance, setCreditBalance] = useState({});
  const [creditHistory, setCreditHistory] = useState([]);
  const [refundingId,   setRefundingId]   = useState(null);

  // Load all three data sources in parallel on mount. If any fails
  // we still render the page — the failed section just stays empty.
  const loadAll = async () => {
    setLoading(true);
    try {
      const [payRes, balRes, histRes] = await Promise.all([
        api.get('/payments/my').catch(() => ({ data: [] })),
        api.get('/credits/balance').catch(() => ({ data: { byCurrency: {} } })),
        api.get('/credits/history').catch(() => ({ data: { credits: [] } })),
      ]);
      setPayments(Array.isArray(payRes.data) ? payRes.data : []);
      setCreditBalance(balRes.data?.byCurrency || {});
      setCreditHistory(histRes.data?.credits || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  // Refund-to-card. Matches the behavior already in ProfilePage so a
  // user who hits this from either surface sees identical UX. A native
  // window.confirm guard prevents an accidental click from starting a
  // 5-10 day Stripe refund the user can't easily undo.
  const requestCreditRefund = async (credit) => {
    const amt = fmtAmount(credit.amount_cents, credit.currency);
    if (!window.confirm(
      `Refund ${amt} back to the card you paid with?\n\n` +
      `This will initiate a Stripe refund. It may take 5-10 business days to appear on your statement.`
    )) return;
    setRefundingId(credit.id);
    try {
      const { data } = await api.post(`/credits/${credit.id}/refund`);
      toast.success(data.message || `Refund of ${amt} initiated`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Refund failed. Try again or contact support.');
    }
    setRefundingId(null);
  };

  const hasBalance    = Object.values(creditBalance).some(v => v > 0);
  const hasPayments   = payments.length > 0;
  const hasCreditHist = creditHistory.length > 0;
  const isEmpty       = !loading && !hasBalance && !hasPayments && !hasCreditHist;

  return (
    <div className="animate-fade-in" style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1 className="section-title text-4xl mb-2">Activity</h1>
      <p className="text-secondary text-sm mb-6">
        Your payments, credits, and refund history across all teams.
      </p>

      {loading && (
        <div className="card text-center py-8">
          <div className="spinner mx-auto mb-3" />
          <p className="text-secondary text-sm">Loading your activity…</p>
        </div>
      )}

      {/* ── Empty state ──
          Only reachable if a user navigates to /activity directly
          while having zero payments and zero credits — the nav item
          itself is hidden in that case. Kept for bookmark/deep-link
          robustness. */}
      {isEmpty && (
        <div className="card text-center py-10">
          <div style={{ fontSize: 40, marginBottom: 12 }}>💳</div>
          <h2 className="font-bold mb-2">No activity yet</h2>
          <p className="text-secondary text-sm" style={{ maxWidth: 420, margin: '0 auto' }}>
            When you pay a spare fee or season fee, you'll see the payment here —
            along with any credits you earn from checking out of a paid game.
          </p>
        </div>
      )}

      {/* ── Credit balance ──
          Shown first because it's actionable: the spare can see at a
          glance what they can spend on the next game. Per-currency
          chips in case a user has played on a CAD team and a USD
          team (rare but supported end-to-end by the payment layer). */}
      {!loading && hasBalance && (
        <div className="card mb-5">
          <div className="flex items-center gap-2 mb-3">
            <span style={{ fontSize: 18 }}>🎟</span>
            <h2 className="text-lg font-bold">Credit Balance</h2>
          </div>
          <p className="text-secondary text-xs mb-4 -mt-1">
            Credits auto-apply the next time you check into a paid spare game on
            the same team. You can also request a refund back to your card below.
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(creditBalance)
              .filter(([, cents]) => cents > 0)
              .map(([cur, cents]) => (
                <div key={cur}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg"
                  style={{
                    background: 'rgba(0,232,122,0.10)',
                    border: '1px solid rgba(0,232,122,0.30)',
                  }}>
                  <CreditCard size={14} style={{ color: 'var(--goal)' }} />
                  <span className="font-bold" style={{ color: 'var(--goal)', fontSize: 14 }}>
                    {fmtAmount(cents, cur)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ── Payments ──
          Chronological list of every payment row, newest first. Paid
          + pending + failed all show so a user can tell whether a
          recent checkout actually landed. Each row is tappable if
          there's a gameId, so the user can jump straight to the game
          the fee was for. */}
      {!loading && hasPayments && (
        <div className="card mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Receipt size={18} style={{ color: 'var(--ice)' }} />
            <h2 className="text-lg font-bold">Payments</h2>
          </div>
          <div className="space-y-2">
            {payments.map(p => {
              const badge = PAYMENT_STATUS_BADGE[p.status]
                || { text: p.status || 'Unknown', bg: 'var(--surface-2)', fg: 'var(--text-2)', border: 'var(--border)' };
              const gameDateLabel = fmtDate(p.gameDate);
              const whenLabel     = fmtTimestamp(p.paidAt || p.createdAt);
              const clickable     = !!p.gameId;
              const rowContext    = [
                p.teamName || null,
                gameDateLabel ? `Game · ${gameDateLabel}` : null,
              ].filter(Boolean).join(' · ');
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={clickable ? () => navigate(`/game/${p.gameId}`) : undefined}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-lg"
                  style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    cursor: clickable ? 'pointer' : 'default',
                  }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">
                        {fmtAmount(p.amountCents, p.currency)}
                      </span>
                      <span className="text-xs text-secondary">
                        · {paymentTypeLabel(p.paymentType)}
                      </span>
                      <span
                        className="badge text-xs flex-shrink-0"
                        style={{ background: badge.bg, color: badge.fg, border: `1px solid ${badge.border}`, fontWeight: 700 }}>
                        {badge.text}
                      </span>
                    </div>
                    {rowContext && (
                      <div className="text-xs text-secondary mt-0.5 truncate">{rowContext}</div>
                    )}
                    {whenLabel && (
                      <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                        {p.status === 'paid' ? 'Paid' : 'Started'} {whenLabel}
                      </div>
                    )}
                  </div>
                  {clickable && (
                    <ChevronRight size={16} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Credit history ──
          Full audit trail of credits minted (from check-out), used
          (against a later game), and refunded (sent to card). The
          refund-to-card action is surfaced only on `available` rows
          — applied rows have already been consumed, refunded rows
          already went back to the card. */}
      {!loading && hasCreditHist && (
        <div className="card mb-5">
          <div className="flex items-center gap-2 mb-3">
            <RefreshCw size={18} style={{ color: '#a78bfa' }} />
            <h2 className="text-lg font-bold">Credit History</h2>
          </div>
          <div className="space-y-2">
            {creditHistory.map(c => {
              const amt = fmtAmount(c.amount_cents, c.currency);
              const badge = CREDIT_STATUS_BADGE[c.status]
                || { text: c.status, bg: 'var(--surface-2)', fg: 'var(--text-2)', border: 'var(--border)' };
              const sourceLabel = c.source_team_name
                ? `From ${c.source_team_name}${c.source_game_date ? ` · ${fmtDate(c.source_game_date)}` : ''}`
                : 'From past payment';
              const appliedLabel = c.status === 'applied' && c.applied_to_team_name
                ? `Used on ${c.applied_to_team_name}${c.applied_to_game_date ? ` · ${fmtDate(c.applied_to_game_date)}` : ''}`
                : null;
              return (
                <div key={c.id}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg"
                  style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">{amt}</span>
                      <span
                        className="badge text-xs flex-shrink-0"
                        style={{ background: badge.bg, color: badge.fg, border: `1px solid ${badge.border}`, fontWeight: 700 }}>
                        {badge.text}
                      </span>
                    </div>
                    <div className="text-xs text-secondary mt-0.5 truncate">
                      {sourceLabel}
                    </div>
                    {appliedLabel && (
                      <div className="text-xs mt-0.5 truncate" style={{ color: '#a78bfa' }}>
                        → {appliedLabel}
                      </div>
                    )}
                  </div>
                  {c.status === 'available' && (
                    <button
                      onClick={() => requestCreditRefund(c)}
                      disabled={refundingId === c.id}
                      className="text-xs font-bold px-2.5 py-1.5 rounded-lg flex-shrink-0"
                      style={{
                        background: 'var(--surface-3)',
                        color: 'var(--text-2)',
                        border: '1px solid var(--border)',
                        minHeight: 28,
                      }}>
                      {refundingId === c.id ? '…' : 'Refund to card'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
